import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sparkles, RefreshCw } from 'lucide-react';
import TrafficSourcesCard from '../components/TrafficSourcesCard';

const MarketingDashboard = () => {
  // AI Insights state
  const [aiInsights, setAiInsights] = useState(null);
  const [aiInsightsLoading, setAiInsightsLoading] = useState(false);
  const [aiInsightsTimestamp, setAiInsightsTimestamp] = useState(null);

  const mountedRef = useRef(true);

  useEffect(() => {
    fetchAiInsights();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getAuthHeaders = async () => {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.access_token) return null;
    return { Authorization: `Bearer ${session.session.access_token}` };
  };

  const fetchAiInsights = async (refresh = false) => {
    setAiInsightsLoading(true);
    try {
      const headers = await getAuthHeaders();
      if (!headers) return;
      const response = await api.get(`/api/admin/analytics/ai-insights${refresh ? '?refresh=true' : ''}`, { headers });
      if (!mountedRef.current) return;
      setAiInsights(response.data.insights || []);
      setAiInsightsTimestamp(response.data.generated_at);
    } catch (err) {
      console.error('AI insights fetch error:', err);
    } finally {
      if (mountedRef.current) setAiInsightsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Marketing</h2>
        <p className="text-muted-foreground">AI-generated insights and recommendations</p>
      </div>

      {/* ===== TRAFFIC SOURCES (our own attribution records) ===== */}
      <TrafficSourcesCard />

      {/* ===== AI INSIGHTS ===== */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <h3 className="text-lg font-semibold">AI Insights</h3>
          {aiInsightsTimestamp && (
            <span className="text-xs text-muted-foreground ml-2">
              Generated {new Date(aiInsightsTimestamp).toLocaleString()}
            </span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={aiInsightsLoading}
          onClick={() => fetchAiInsights(true)}
        >
          <RefreshCw className={`h-4 w-4 mr-1 ${aiInsightsLoading ? 'animate-spin' : ''}`} />
          {aiInsightsLoading ? 'Generating...' : 'Refresh'}
        </Button>
      </div>

      {aiInsightsLoading && !aiInsights && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-32 bg-muted rounded-lg" />
            </div>
          ))}
        </div>
      )}

      {aiInsights && aiInsights.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {aiInsights.map((insight, i) => (
            <Card key={i} className={`border-l-4 ${
              insight.priority === 'high' ? 'border-l-red-500' :
              insight.priority === 'medium' ? 'border-l-yellow-500' : 'border-l-green-500'
            }`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-sm font-semibold">{insight.title}</CardTitle>
                  <div className="flex gap-1 shrink-0">
                    <Badge variant="outline" className={`text-xs ${
                      insight.priority === 'high' ? 'border-red-300 text-red-700 bg-red-50' :
                      insight.priority === 'medium' ? 'border-yellow-300 text-yellow-700 bg-yellow-50' :
                      'border-green-300 text-green-700 bg-green-50'
                    }`}>
                      {insight.priority}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {(insight.category || '').replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">{insight.description}</p>
                <div className="bg-blue-50 dark:bg-blue-950/30 p-2 rounded text-xs">
                  <span className="font-medium">Action:</span> {insight.action}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

    </div>
  );
};

export default MarketingDashboard;
